/**
 * Pure helpers for building the side-by-side compare-sales table rows.
 * Lives in `/utils` (no JSX) so multiple components / hooks can reuse
 * the row-shape and the matched/unmatched fold without re-implementing
 * it. The compare-view component renders these rows; future surfaces
 * (e.g. a Skimmer or Pooltrackr alignment view) can pair against the
 * same `DisplayRow` shape by swapping the source/counterpart payloads.
 */
import type {
  CompareSalesResponse,
  LineItemLS,
  LineItemXero,
  LineItemPair,
} from '../services/inspector/api'

export interface DisplayRow {
  lsItem?: LineItemLS
  xeroItem?: LineItemXero
  pair?: LineItemPair
  isUnmatchedLs?: boolean
  isUnmatchedXero?: boolean
  isWarningOnly?: boolean
  isError: boolean
  isWarning: boolean
  priceMismatch: boolean
  qtyMismatch: boolean
  descriptionMismatch: boolean
}

/**
 * Fold a `CompareSalesResponse` into a flat list of display rows. Order:
 *   1. Matched pairs sorted by absolute LS amount (largest first).
 *   2. Unmatched LS lines.
 *   3. Unmatched Xero lines.
 *
 * Warning-only unmatched lines are tagged via the `isWarningOnly` flag
 * so the renderer can downgrade their styling without dropping them.
 */
export const buildDisplayRows = (data: CompareSalesResponse): DisplayRow[] => {
  const { lightspeed, xero, comparison } = data
  const rows: DisplayRow[] = []
  const unmatchedLsWarnings = new Set(comparison.unmatched_ls_warnings || [])
  const unmatchedXeroWarnings = new Set(comparison.unmatched_xero_warnings || [])

  for (const pair of comparison.pairs) {
    const priceMismatch = !pair.price_match
    const qtyMismatch = !pair.qty_match
    const descriptionMismatch = !pair.description_match
    const isError = priceMismatch
    const isWarning = !isError && (qtyMismatch || descriptionMismatch)
    rows.push({
      lsItem: lightspeed.items[pair.ls_index],
      xeroItem: xero.items[pair.xero_index],
      pair,
      isError,
      isWarning,
      priceMismatch,
      qtyMismatch,
      descriptionMismatch,
    })
  }

  rows.sort((a, b) => {
    const aValue = Math.abs(a.lsItem?.amount_ex_tax || 0)
    const bValue = Math.abs(b.lsItem?.amount_ex_tax || 0)
    return bValue - aValue
  })

  for (const lsIndex of comparison.unmatched_ls_indices) {
    const isWarningOnly = unmatchedLsWarnings.has(lsIndex)
    rows.push({
      lsItem: lightspeed.items[lsIndex],
      isUnmatchedLs: true,
      isWarningOnly,
      isError: !isWarningOnly,
      isWarning: isWarningOnly,
      priceMismatch: false,
      qtyMismatch: false,
      descriptionMismatch: false,
    })
  }

  for (const xeroIndex of comparison.unmatched_xero_indices) {
    const isWarningOnly = unmatchedXeroWarnings.has(xeroIndex)
    rows.push({
      xeroItem: xero.items[xeroIndex],
      isUnmatchedXero: true,
      isWarningOnly,
      isError: !isWarningOnly,
      isWarning: isWarningOnly,
      priceMismatch: false,
      qtyMismatch: false,
      descriptionMismatch: false,
    })
  }

  return rows
}

// Synthetic notes (LS or Xero side flagged is_synthetic_note) are
// pulled out of the line-items section and surfaced in the dedicated
// Notes row above Totals - they aren't sales lines and shouldn't
// crowd the line-item body.
export const isSyntheticNoteRow = (r: DisplayRow): boolean =>
  !!r.lsItem?.is_synthetic_note || !!r.xeroItem?.is_synthetic_note
